import React from 'react';

/*
 * iter132 (Feb 2026) — Structured payment terms picker used by the Mark-Won
 * modal (and reused in read-only mode by the Approvals page). Four canonical
 * legs, each with an enable checkbox + percent input. Credit also captures a
 * net-days field. The parent owns state; this component is a pure controlled
 * input so the parent can drive validation + submit.
 *
 * Value shape:
 *   {
 *     advance_with_po: { enabled, percent },
 *     before_dispatch: { enabled, percent },
 *     on_installation: { enabled, percent },
 *     credit:          { enabled, percent, days },
 *   }
 */

export const emptyPaymentTerms = () => ({
  advance_with_po: { enabled: true, percent: 50 },
  before_dispatch: { enabled: true, percent: 50 },
  on_installation: { enabled: false, percent: 0 },
  credit: { enabled: false, percent: 0, days: 30 },
});

const LEGS = [
  { key: 'advance_with_po', label: 'Advance with PO',   note: 'Standard leg — enabled by default' },
  { key: 'before_dispatch', label: 'Before Dispatch',   note: 'Standard leg — enabled by default' },
  { key: 'on_installation', label: 'On Installation',   note: 'Needs VP approval when enabled' },
  { key: 'credit',          label: 'Credit',            note: 'Discouraged — always needs VP approval', discouraged: true },
];

export const isStandardTerms = (v) => {
  if (!v) return false;
  const adv = v.advance_with_po?.enabled && Number(v.advance_with_po?.percent) > 0;
  const disp = v.before_dispatch?.enabled && Number(v.before_dispatch?.percent) > 0;
  const inst = v.on_installation?.enabled && Number(v.on_installation?.percent) > 0;
  const cred = v.credit?.enabled && Number(v.credit?.percent) > 0;
  if (!adv || !disp) return false;
  if (inst || cred) return false;
  const total = Number(v.advance_with_po?.percent || 0) + Number(v.before_dispatch?.percent || 0);
  return Math.abs(total - 100) < 0.01;
};

export const totalPercent = (v) => {
  if (!v) return 0;
  let t = 0;
  ['advance_with_po', 'before_dispatch', 'on_installation', 'credit'].forEach(k => {
    if (v[k]?.enabled) t += Number(v[k]?.percent || 0);
  });
  return t;
};

// iter132.2 — Canonical key for a structured-terms combo. Mirrors the
// backend `_pt_canonical_key` exactly (percents formatted with `.2f`) so a
// client-side match check reports the same truth the server will apply on
// submit.
export const canonicalTermsKey = (v) => {
  if (!v) return '';
  const parts = [];
  ['advance_with_po', 'before_dispatch', 'on_installation', 'credit'].forEach(k => {
    const leg = v[k] || {};
    if (!leg.enabled) return;
    const pct = Math.round(Number(leg.percent || 0) * 100) / 100;
    const pctStr = pct.toFixed(2);
    if (k === 'credit') {
      parts.push(`${k}:${pctStr}:${Number(leg.days || 0) | 0}`);
    } else {
      parts.push(`${k}:${pctStr}`);
    }
  });
  return parts.join('|');
};

export default function PaymentTermsPicker({ value, onChange, disabled = false }) {
  const v = value || emptyPaymentTerms();

  const patch = (key, patchObj) => {
    onChange({ ...v, [key]: { ...v[key], ...patchObj } });
  };

  return (
    <div className="border border-[#D5E8E4] rounded-lg overflow-hidden" data-testid="payment-terms-picker">
      <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] bg-[#F3F9F7] px-3 py-2 border-b border-[#D5E8E4]">
        <div>Include</div>
        <div>Term</div>
        <div className="text-right">%</div>
        <div className="text-right">Days</div>
      </div>
      {LEGS.map((leg) => {
        const legVal = v[leg.key] || { enabled: false, percent: 0 };
        const isCredit = leg.key === 'credit';
        return (
          <div
            key={leg.key}
            className={`grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-3 py-2 border-b border-[#EAF3F0] last:border-b-0 ${
              legVal.enabled ? 'bg-white' : 'bg-[#FCFEFD]'
            }`}
            data-testid={`payment-terms-row-${leg.key}`}
          >
            <input
              type="checkbox"
              checked={!!legVal.enabled}
              disabled={disabled}
              onChange={(e) => patch(leg.key, { enabled: e.target.checked, percent: e.target.checked ? (legVal.percent || 0) : 0 })}
              className="w-4 h-4 accent-[#00a69c]"
              data-testid={`payment-terms-checkbox-${leg.key}`}
            />
            <div>
              <div className={`text-sm font-medium ${leg.discouraged ? 'text-rose-700' : 'text-[#052A23]'}`}>
                {leg.label}
              </div>
              <div className="text-[11px] text-[#6D8E87]">{leg.note}</div>
            </div>
            <div className="w-24">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                inputMode="decimal"
                disabled={disabled || !legVal.enabled}
                value={legVal.enabled ? (legVal.percent ?? '') : ''}
                onChange={(e) => patch(leg.key, { percent: e.target.value === '' ? 0 : Number(e.target.value) })}
                className="w-full px-2 py-1.5 text-sm text-right tabular-nums border border-[#D5E8E4] rounded focus:ring-2 focus:ring-[#00bfb3] outline-none disabled:bg-[#F3F9F7] disabled:text-[#B6C9C4]"
                placeholder="0"
                data-testid={`payment-terms-percent-${leg.key}`}
              />
            </div>
            <div className="w-20 text-right">
              {isCredit ? (
                <input
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  inputMode="numeric"
                  disabled={disabled || !legVal.enabled}
                  value={legVal.enabled ? (legVal.days ?? '') : ''}
                  onChange={(e) => patch(leg.key, { days: e.target.value === '' ? 0 : Number(e.target.value) })}
                  className="w-full px-2 py-1.5 text-sm text-right tabular-nums border border-[#D5E8E4] rounded focus:ring-2 focus:ring-[#00bfb3] outline-none disabled:bg-[#F3F9F7] disabled:text-[#B6C9C4]"
                  placeholder="30"
                  data-testid="payment-terms-credit-days"
                />
              ) : (
                <span className="text-[11px] text-[#B6C9C4]">—</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
